import React, { useState, useEffect } from 'react';
import { UserPlus, Check, X, Clock, Mail, Trash2, Filter, RefreshCw } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export default function AdminAccessRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    loadRequests();
  }, [filter]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAccessRequests(filter === 'all' ? null : filter);
      setRequests(response.data.requests || []);
      setCounts(response.data.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (error) {
      console.error('Failed to load access requests:', error);
      toast.error('Failed to load access requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'approving' }));
    try {
      await adminAPI.approveAccessRequest(requestId);
      toast.success('Access approved! Invitation email sent.');
      loadRequests();
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleReject = async (requestId) => {
    setProcessing(prev => ({ ...prev, [requestId]: 'rejecting' }));
    try {
      await adminAPI.rejectAccessRequest(requestId);
      toast.success('Request rejected');
      loadRequests();
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error('Failed to reject request');
    } finally {
      setProcessing(prev => ({ ...prev, [requestId]: null }));
    }
  };

  const handleDelete = async (requestId) => {
    try {
      await adminAPI.deleteAccessRequest(requestId);
      toast.success('Request deleted');
      loadRequests();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete request');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#E8C495]/20 text-[#E8C495] rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#8DA399]/20 text-[#8DA399] rounded-full text-xs font-medium">
            <Check className="w-3 h-3" /> Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-600 rounded-full text-xs font-medium">
            <X className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-access-requests">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl text-[#4A2C2A]">Access Requests</h1>
            <p className="text-[#4A2C2A]/60 text-sm mt-1">
              Manage user access requests and send invitations
            </p>
          </div>
          <Button
            variant="outline"
            onClick={loadRequests}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div 
            className={`soft-card p-4 cursor-pointer transition-all ${filter === 'pending' ? 'ring-2 ring-[#E8C495]' : ''}`}
            onClick={() => setFilter('pending')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A2C2A]/60 text-sm">Pending</p>
                <p className="text-2xl font-bold text-[#E8C495]">{counts.pending}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#E8C495]/20 flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#E8C495]" />
              </div>
            </div>
          </div>
          
          <div 
            className={`soft-card p-4 cursor-pointer transition-all ${filter === 'approved' ? 'ring-2 ring-[#8DA399]' : ''}`}
            onClick={() => setFilter('approved')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A2C2A]/60 text-sm">Approved</p>
                <p className="text-2xl font-bold text-[#8DA399]">{counts.approved}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#8DA399]" />
              </div>
            </div>
          </div>
          
          <div 
            className={`soft-card p-4 cursor-pointer transition-all ${filter === 'rejected' ? 'ring-2 ring-red-400' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#4A2C2A]/60 text-sm">Rejected</p>
                <p className="text-2xl font-bold text-red-500">{counts.rejected}</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <X className="w-5 h-5 text-red-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-[#4A2C2A]/50" />
            <span className="text-sm text-[#4A2C2A]/70">Filter:</span>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requests List */}
        <div className="soft-card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-12 h-12 text-[#4A2C2A]/20 mx-auto mb-4" />
              <p className="text-[#4A2C2A]/60">No {filter !== 'all' ? filter : ''} access requests</p>
            </div>
          ) : (
            <div className="divide-y divide-[#E8A095]/10">
              {requests.map((request) => (
                <div key={request.id} className="p-4 hover:bg-[#FDFBF7]/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium text-[#4A2C2A]">{request.name}</h3>
                        {getStatusBadge(request.status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[#4A2C2A]/60">
                        <Mail className="w-3.5 h-3.5" />
                        {request.email}
                      </div>
                      {request.reason && (
                        <p className="mt-2 text-sm text-[#4A2C2A]/70 bg-[#FDFBF7] p-2 rounded-lg">
                          "{request.reason}"
                        </p>
                      )}
                      <p className="text-xs text-[#4A2C2A]/40 mt-2">
                        Requested {formatDate(request.created_at)}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {request.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(request.id)}
                            disabled={processing[request.id]}
                            className="bg-[#8DA399] hover:bg-[#7a9389] text-white"
                            data-testid={`approve-${request.id}`}
                          >
                            {processing[request.id] === 'approving' ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" /> Approve
                              </>
                            )}
                          </Button>
                          
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={processing[request.id]}
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                data-testid={`reject-${request.id}`}
                              >
                                <X className="w-4 h-4 mr-1" /> Reject
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Reject Access Request?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will reject the access request from {request.name} ({request.email}).
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleReject(request.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Reject
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                      
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Request?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently delete this access request.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(request.id)}
                              className="bg-red-500 hover:bg-red-600"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
